import React, { useCallback, useEffect, useRef, useState } from 'react';
import './poster-carousel.css';

// Extend Window interface for Instagram embed script
declare global {
  interface Window {
    instgrm?: {
      Embeds: {
        process: () => void;
      };
    };
  }
}

const YOUTUBE_PLAYING_STATES = new Set([1, 3]); // PLAYING, BUFFERING

// Instagram posts from @s_cubus_dwarka
// Update these URLs with your latest 3 posts every few days
// To get URLs: Go to Instagram → Click post → Copy link
const INSTAGRAM_POST_URLS = [
  'https://www.instagram.com/p/DYv6NW5F0sp/',
  'https://www.instagram.com/p/DYloF8ZF0JJ/',
  'https://www.instagram.com/p/DYeDzviGprQ/',
];

const INSTAGRAM_POST_COUNT = 3;
const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || 'https://scubus.onrender.com/api'
).replace(/\/$/, '');

// YouTube video IDs for second carousel — fetched dynamically from backend RSS
const YOUTUBE_VIDEO_COUNT = 3;

interface YouTubePostData {
  id: string;
  title: string;
  url: string;
  thumbnailUrl: string;
  timestamp: string;
}

// Fallback video IDs if API is unavailable
const FALLBACK_VIDEO_IDS = [
  'Y5xzUkW_AHk',
  'vpKIetcXvVA',
  '2I66kh_5tik',
];

interface InstagramPostData {
  id: string;
  shortcode: string;
  url: string;
  imageUrl: string;
  media_url?: string; // Video URL for VIDEO posts
  thumbnail_url?: string; // Thumbnail for VIDEO posts
  type?: 'VIDEO' | 'IMAGE' | 'CAROUSEL_ALBUM'; // Media type
  caption: string;
  timestamp: number;
  likes?: number;
  comments?: number;
}

const AUTOPLAY_MS = 5000;
const SWIPE_THRESHOLD = 50; // px

interface YouTubeEmbedProps {
  videoId: string;
  title: string;
  onPlayingChange: (playing: boolean) => void;
}

const YouTubeEmbed: React.FC<YouTubeEmbedProps> = ({
  videoId,
  title,
  onPlayingChange,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://www.youtube.com') return;

      const iframe = iframeRef.current;
      if (!iframe?.contentWindow || event.source !== iframe.contentWindow) return;

      try {
        const data =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (data?.event === 'onStateChange') {
          onPlayingChange(YOUTUBE_PLAYING_STATES.has(data.info));
          return;
        }

        if (
          data?.event === 'infoDelivery' &&
          typeof data.info?.playerState === 'number'
        ) {
          onPlayingChange(YOUTUBE_PLAYING_STATES.has(data.info.playerState));
        }
      } catch {
        // Ignore unrelated postMessage payloads.
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      onPlayingChange(false);
    };
  }, [videoId, onPlayingChange]);

  return (
    <iframe
      ref={iframeRef}
      className="poster-video"
      src={`https://www.youtube.com/embed/${videoId}?rel=0&enablejsapi=1&playsinline=1&origin=${encodeURIComponent(window.location.origin)}`}
      title={title}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
    />
  );
};

const getIsTouchDevice = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(hover: none), (pointer: coarse)').matches;

const PosterCarousel: React.FC = () => {
  // Instagram posts state
  const [instagramPosts, setInstagramPosts] = useState<InstagramPostData[]>([]);
  const [loading, setLoading] = useState(true);

  // YouTube posts state
  const [youtubePosts, setYoutubePosts] = useState<YouTubePostData[]>([]);
  const [youtubeLoading, setYoutubeLoading] = useState(true);
  
  // Modal state for expanded view
  const [expandedPost, setExpandedPost] = useState<{
    imageUrl?: string;
    media_url?: string;
    thumbnail_url?: string;
    media_type?: string;
    url: string;
    caption?: string;
  } | null>(null);
  
  // First carousel state
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  // Second carousel state
  const [index2, setIndex2] = useState(0);
  const [paused2, setPaused2] = useState(false);
  const [youtubePlaying, setYoutubePlaying] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(getIsTouchDevice);
  const touchStartX2 = useRef<number | null>(null);
  const youtubePlayingRef = useRef(false);

  useEffect(() => {
    youtubePlayingRef.current = youtubePlaying;
  }, [youtubePlaying]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(hover: none), (pointer: coarse)');
    const updateTouchDevice = () => setIsTouchDevice(mediaQuery.matches);
    updateTouchDevice();
    mediaQuery.addEventListener('change', updateTouchDevice);
    return () => mediaQuery.removeEventListener('change', updateTouchDevice);
  }, []);

  useEffect(() => {
    setYoutubePlaying(false);
  }, [index2]);

  const handleYouTubePlayingChange = useCallback((playing: boolean) => {
    setYoutubePlaying((prev) => (prev === playing ? prev : playing));
    if (playing) {
      setPaused2(true);
    } else if (!touchStartX2.current) {
      setPaused2(false);
    }
  }, []);

  // Fetch Instagram posts on mount
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const apiUrl = `${API_BASE_URL}/instagram/posts`;
          
        const response = await fetch(
          `${apiUrl}?count=${INSTAGRAM_POST_COUNT}`
        );
        const data = await response.json();
        
        if (data.success && data.posts && data.posts.length > 0) {
          setInstagramPosts(data.posts);
        } else {
          // Fallback: Use manual URLs with image extraction
          const fallbackPosts = INSTAGRAM_POST_URLS.map((url: string, i: number) => {
            const shortcode = extractShortcode(url);
            return {
              id: `fallback-${i}`,
              shortcode: shortcode,
              url: url,
              imageUrl: `https://www.instagram.com/p/${shortcode}/media/?size=l`,
              caption: '',
              timestamp: Date.now(),
            };
          });
          setInstagramPosts(fallbackPosts);
        }
      } catch (error) {
        console.error('Failed to fetch Instagram posts:', error);
        // Use fallback with extracted images
        const fallbackPosts = INSTAGRAM_POST_URLS.map((url: string, i: number) => {
          const shortcode = extractShortcode(url);
          return {
            id: `fallback-${i}`,
            shortcode: shortcode,
            url: url,
            imageUrl: `https://www.instagram.com/p/${shortcode}/media/?size=l`,
            caption: '',
            timestamp: Date.now(),
          };
        });
        setInstagramPosts(fallbackPosts);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Fetch YouTube posts on mount
  useEffect(() => {
    const fetchYouTubePosts = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/youtube/posts?count=${YOUTUBE_VIDEO_COUNT}`);
        const data = await response.json();
        if (data.success && data.posts && data.posts.length > 0) {
          setYoutubePosts(data.posts);
        } else {
          // Fallback to hardcoded IDs
          setYoutubePosts(FALLBACK_VIDEO_IDS.map((id, i) => ({
            id,
            title: `S-CUBUS Video ${i + 1}`,
            url: `https://www.youtube.com/watch?v=${id}`,
            thumbnailUrl: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
            timestamp: '',
          })));
        }
      } catch {
        setYoutubePosts(FALLBACK_VIDEO_IDS.map((id, i) => ({
          id,
          title: `S-CUBUS Video ${i + 1}`,
          url: `https://www.youtube.com/watch?v=${id}`,
          thumbnailUrl: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
          timestamp: '',
        })));
      } finally {
        setYoutubeLoading(false);
      }
    };
    fetchYouTubePosts();
  }, []);

  // Helper function to extract shortcode from URL
  const extractShortcode = (url: string): string => {
    const match = url.match(/\/p\/([^/?]+)/);
    return match ? match[1] : '';
  };

  // Create slides from Instagram posts
  const slides = instagramPosts.map((post, i) => ({
    type: 'instagram' as const,
    url: post.url,
    imageUrl: post.imageUrl,
    media_url: post.media_url,
    thumbnail_url: post.thumbnail_url,
    media_type: post.type,
    caption: post.caption,
    alt: `S-CUBUS Instagram post ${i + 1}`,
  }));

  // Create slides from YouTube posts
  const slides2 = youtubePosts.map((post, i) => ({
    type: 'video' as const,
    id: post.id,
    alt: post.title || `S-CUBUS video ${i + 1}`,
  }));

  // Helper function for slide keys
  const slideKey = (slide: any, index: number) => {
    if ('url' in slide && slide.type === 'instagram') return `instagram-${index}`;
    if (slide.type === 'video') return `video-${index}`;
    if ('src' in slide) return slide.src;
    return `slide-${index}`;
  };

  const getYouTubeThumbnailUrl = (videoId: string) => {
    const post = youtubePosts.find(p => p.id === videoId);
    return post?.thumbnailUrl || `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  };

  const count = slides.length;
  const goTo = (i: number) => setIndex((i + count) % count);
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  // Second carousel controls
  const count2 = slides2.length;
  const goTo2 = (i: number) => setIndex2((i + count2) % count2);
  const next2 = () => goTo2(index2 + 1);
  const prev2 = () => goTo2(index2 - 1);

  // Video error handling - fallback to thumbnail on load failure
  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    console.error('Video failed to load, falling back to thumbnail');
    const videoElement = e.currentTarget;
    const thumbnailUrl = videoElement.getAttribute('data-thumbnail');
    
    // Hide video and show fallback image
    if (videoElement.parentElement) {
      videoElement.style.display = 'none';
      
      // Create fallback image if thumbnail exists
      if (thumbnailUrl) {
        const fallbackImg = document.createElement('img');
        fallbackImg.src = thumbnailUrl;
        fallbackImg.alt = 'Video thumbnail (playback unavailable)';
        fallbackImg.className = 'instagram-post-image';
        videoElement.parentElement.insertBefore(fallbackImg, videoElement);
      }
    }
  };

  // Load Instagram embed script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.instagram.com/embed.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Process Instagram embeds when slides change
  useEffect(() => {
    if (window.instgrm && !loading) {
      window.instgrm.Embeds.process();
    }
  }, [index, loading]);

  // Auto-advance, paused on hover / touch.
  useEffect(() => {
    if (paused || count <= 1 || loading) return;
    const id = window.setTimeout(next, AUTOPLAY_MS);
    return () => window.clearTimeout(id);
  }, [index, paused, count, loading]);

  // Auto-advance for YouTube carousel only. Disabled on mobile.
  useEffect(() => {
    if (isTouchDevice || paused2 || youtubePlaying || count2 <= 1) return;
    const id = window.setTimeout(next2, AUTOPLAY_MS);
    return () => window.clearTimeout(id);
  }, [index2, paused2, youtubePlaying, count2, isTouchDevice]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    setPaused(true);
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta > SWIPE_THRESHOLD) prev();
    else if (delta < -SWIPE_THRESHOLD) next();
    touchStartX.current = null;
    setPaused(false);
  };

  // Touch handlers for second carousel
  const onTouchStart2 = (e: React.TouchEvent) => {
    touchStartX2.current = e.touches[0].clientX;
    setPaused2(true);
  };

  const handleYouTubeSwipeEnd = (e: React.TouchEvent) => {
    if (touchStartX2.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX2.current;
    if (delta > SWIPE_THRESHOLD) prev2();
    else if (delta < -SWIPE_THRESHOLD) next2();
    touchStartX2.current = null;
    if (!youtubePlayingRef.current) {
      setPaused2(false);
    }
  };

  const onTouchEnd2 = handleYouTubeSwipeEnd;

  if (loading) {    return (
      <section className="posters-section" aria-label="Posters">
        <div className="posters-heading">
          <h2>Latest Updates</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#5b6478' }}>
          <p>Loading Instagram posts...</p>
        </div>
      </section>
    );
  }

  if (count === 0) {
    return (
      <section className="posters-section" aria-label="Posters">
        <div className="posters-heading">
          <h2>Latest Updates</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#5b6478' }}>
          <p>No posts available</p>
        </div>
      </section>
    );
  }

  return (
    <section className="posters-section" aria-label="Posters">
      <div className="posters-heading">
        <h2>Latest Updates</h2>
      </div>

      {/* Expanded Post Modal */}
      {expandedPost && (
        <div 
          className="instagram-modal-overlay" 
          onClick={() => setExpandedPost(null)}
        >
          <div 
            className="instagram-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="instagram-modal-close"
              onClick={() => setExpandedPost(null)}
              aria-label="Close"
            >
              ✕
            </button>
            
            {expandedPost.media_type === 'VIDEO' && expandedPost.media_url ? (
              <video
                src={expandedPost.media_url}
                poster={expandedPost.thumbnail_url || expandedPost.imageUrl}
                controls
                controlsList="nodownload"
                autoPlay
                className="instagram-modal-media"
                playsInline
                preload="auto"
                style={{ display: 'block' }}
              >
                Your browser does not support video playback.
              </video>
            ) : (
              <img
                src={expandedPost.imageUrl}
                alt={expandedPost.caption || 'Instagram post'}
                className="instagram-modal-media"
              />
            )}
            
            <a
              href={expandedPost.url}
              target="_blank"
              rel="noopener noreferrer"
              className="instagram-modal-link"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ marginRight: '6px' }}>
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              View on Instagram
            </a>
          </div>
        </div>
      )}

      {/* Two Carousels Side by Side */}
      <div className="poster-carousel-container">
        {/* First Carousel */}
        <div className="poster-carousel-wrapper poster-carousel-wrapper--instagram">
          <div
            className="poster-carousel"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <button
              type="button"
              className="poster-nav prev"
              onClick={prev}
              aria-label="Previous poster"
            >
              &#8249;
            </button>

            <div className="poster-viewport">
              <div
                className="poster-track"
                style={{ transform: `translateX(-${index * 100}%)` }}
              >
                {slides.map((slide, i) => (
                  <div
                    className={`poster-slide ${slide.type === 'instagram' ? 'poster-slide--instagram' : ''}`}
                    key={slideKey(slide, i)}
                  >
                    {slide.type === 'instagram' ? (
                      <div
                        className={`instagram-post-card carousel-post-item${slide.media_type === 'VIDEO' ? ' instagram-post-card--video' : ''}`}
                      >
                        {slide.media_type === 'VIDEO' && slide.media_url ? (
                          <>
                            <div className="instagram-post-media">
                              {i === index ? (
                                <video
                                  src={slide.media_url}
                                  poster={slide.thumbnail_url || slide.imageUrl}
                                  controls
                                  controlsList="nodownload"
                                  className="carousel-video"
                                  data-thumbnail={slide.thumbnail_url || slide.imageUrl}
                                  onError={handleVideoError}
                                  preload="metadata"
                                  playsInline
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Your browser does not support video playback.
                                </video>
                              ) : (
                                <img
                                  src={slide.thumbnail_url || slide.imageUrl}
                                  alt={slide.caption || slide.alt}
                                  className="instagram-post-image"
                                  loading={i === 0 ? 'eager' : 'lazy'}
                                  draggable={false}
                                />
                              )}
                            </div>
                            <a
                              href={slide.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="instagram-post-link"
                            >
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ marginRight: '6px' }}>
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                              </svg>
                              View on Instagram
                            </a>
                          </>
                        ) : slide.imageUrl ? (
                          <>
                            <div
                              className="instagram-post-media"
                              onClick={() => setExpandedPost({
                                imageUrl: slide.imageUrl,
                                media_url: slide.media_url,
                                thumbnail_url: slide.thumbnail_url,
                                media_type: slide.media_type,
                                url: slide.url,
                                caption: slide.caption
                              })}
                              style={{ cursor: 'pointer' }}
                            >
                              <img
                                src={slide.imageUrl}
                                alt={slide.caption || slide.alt}
                                className="instagram-post-image"
                                loading={i === 0 ? 'eager' : 'lazy'}
                                draggable={false}
                              />
                            </div>
                            <a
                              href={slide.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="instagram-post-link"
                            >
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ marginRight: '6px' }}>
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                              </svg>
                              View on Instagram
                            </a>
                          </>
                        ) : (
                          <div className="instagram-fallback-card">
                            <div className="instagram-fallback-header">
                              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                              </svg>
                              <span>@s_cubus_dwarka</span>
                            </div>
                            <div className="instagram-fallback-body">
                              <div className="instagram-fallback-image-placeholder">
                                <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor">
                                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                                </svg>
                                <p>Instagram Post</p>
                              </div>
                            </div>
                            <a
                              href={slide.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="instagram-fallback-link"
                            >
                              View Full Post on Instagram →
                            </a>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            <button
              type="button"
              className="poster-nav next"
              onClick={next}
              aria-label="Next poster"
            >
              &#8250;
            </button>
          </div>

          <div className="poster-dots" role="tablist" aria-label="Select poster">
            {slides.map((slide, i) => (
              <button
                type="button"
                key={slideKey(slide, i)}
                className={`poster-dot ${i === index ? 'active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Go to Instagram post ${i + 1}`}
                aria-selected={i === index}
                role="tab"
              />
            ))}
          </div>
        </div>

        {/* Second Carousel */}
        <div className="poster-carousel-wrapper poster-carousel-wrapper--youtube">
          <div
            className="poster-carousel"
            onMouseEnter={() => setPaused2(true)}
            onMouseLeave={() => setPaused2(false)}
            onTouchStart={onTouchStart2}
            onTouchEnd={onTouchEnd2}
          >
            <button
              type="button"
              className="poster-nav prev"
              onClick={prev2}
              aria-label="Previous update"
            >
              &#8249;
            </button>

            <div className="poster-viewport">
              <div
                className="poster-track"
                style={{ transform: `translateX(-${index2 * 100}%)` }}
              >
                {slides2.map((slide, i) => (
                  <div
                    className={`poster-slide ${slide.type === 'video' ? 'poster-slide--video' : ''}`}
                    key={slideKey(slide, i)}
                  >
                    {slide.type === 'video' ? (
                      i === index2 ? (
                        <div className="poster-video-shell">
                          <YouTubeEmbed
                            videoId={slide.id}
                            title={slide.alt}
                            onPlayingChange={handleYouTubePlayingChange}
                          />                          {isTouchDevice && (
                            <>
                              <div
                                className="poster-swipe-edge poster-swipe-edge--left"
                                onTouchStart={onTouchStart2}
                                onTouchEnd={handleYouTubeSwipeEnd}
                                aria-hidden="true"
                              />
                              <div
                                className="poster-swipe-edge poster-swipe-edge--right"
                                onTouchStart={onTouchStart2}
                                onTouchEnd={handleYouTubeSwipeEnd}
                                aria-hidden="true"
                              />
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="poster-video-placeholder" aria-hidden="true">
                          <img
                            src={getYouTubeThumbnailUrl(slide.id)}
                            alt=""
                            loading="lazy"
                            draggable={false}
                          />
                        </div>
                      )
                    ) : 'src' in slide ? (
                      <img
                        src={slide.src as string}
                        alt={slide.alt}
                        loading="lazy"
                        draggable={false}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            <button
              type="button"
              className="poster-nav next"
              onClick={next2}
              aria-label="Next update"
            >
              &#8250;
            </button>
          </div>

          <div className="poster-dots" role="tablist" aria-label="Select update">
            {slides2.map((slide, i) => (
              <button
                type="button"
                key={slideKey(slide, i)}
                className={`poster-dot ${i === index2 ? 'active' : ''}`}
                onClick={() => goTo2(i)}
                aria-label={
                  slide.type === 'video' ? 'Go to update video' : `Go to update ${i + 1}`
                }
                aria-selected={i === index2}
                role="tab"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PosterCarousel;
